import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Form, TabContent, TabPane, Tooltip } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import cx from "classnames";

import global from "../../../scss/dhp.scss";
import { formatProviderDisplayName, preventFormSubmitOnEnter as preventSubmit } from "../../../_helpers/utils";
import {
  DOCUMENT_PERMISSION_OWNER as OWNER,
  DOCUMENT_PERMISSION_FULL as FULL,
  DOCUMENT_PERMISSION_RW as RW,
} from "../../../constants/document";
import { COMPANY_SUPERADMIN } from "../../../constants/company";
import { UNAUTHORIZED_ACCESS } from "../../../constants/thirdParty";
import { CUSTOM, OTHERS, SESSION_KEY_MF_INFO_MODAL_DATA, SYSTEM, TYPE_PROPOSAL } from "../../../constants/editor";

import Modal from "../../ui/modal";
import { Icon } from "../../ui/icon";
import { Input } from "../../ui/input";
import { ProcessLoader } from "../../ui/loader/processLoader";
import TabSchemeSelector from "../../Common/TabSchemeSelector";

import useMergeFields from "../../../hooks/useMergeFields";
import { useCheckCompanyPlanInfo } from "../../../hooks/useCheckCompanyPlanInfo";
import { useCRMAuth } from "../../../hooks/useCRMAuth";

import { EditorContext } from "../../../containers/editor/EditorLayout";
import { getConnectedAccounts } from "../../../store/actions/companyActions";

let style = Object.assign({}, global);

const SCHEME_TYPE_SWITCH_DELAY = 300;
const SCHEME_TYPE_ARRAY_REGULAR = [SYSTEM.NAME, CUSTOM.NAME];
const SCHEME_TYPE_ARRAY_PROPOSAL = [SYSTEM.NAME, CUSTOM.NAME, OTHERS.NAME];

const _helpers = {
  getCleanedValue: str => {
    // collapse multiple spaces + trim removes leading/trailing spaces
    return (str || "").replace(/\s{2,}/g, " ").trim();
  },

  isDataEmpty: ({ data, initialData }) => {
    const isEmpty = ["NULL", null, undefined, initialData].includes(data);
    return isEmpty;
  },
};

const CustomOption = props => {
  const { _idx, option, parentProps } = props;
  const {
    componentsProps: { Option: optionProps },
  } = parentProps;

  const itemRef = useRef(null);
  const [isItemEllipsis, setIsItemEllipsis] = useState(false);
  const [showItemTooltip, setShowItemTooltip] = useState(false);
  const [showEmptyTooltip, setShowEmptyTooltip] = useState(false);

  const toggleItemTooltip = () => setShowItemTooltip(prevState => !prevState);
  const toggleEmptyTooltip = () => setShowEmptyTooltip(prevState => !prevState);

  useEffect(() => {
    setIsItemEllipsis(itemRef.current?.scrollWidth > itemRef.current?.offsetWidth);
  }, []);

  return (
    <div className={cx(style["d-flex"], style["align-items-center"])}>
      <div ref={itemRef} className={style["text-truncate"]}>
        {option.name}
      </div>

      {isItemEllipsis && (
        <Tooltip
          placement="bottom"
          isOpen={showItemTooltip}
          toggle={toggleItemTooltip}
          target={itemRef.current}
          boundariesElement={document.getElementById("app")}>
          {option.name}
        </Tooltip>
      )}

      {option?.isEmpty && (
        <>
          <Icon id={`tooltip-empty-${_idx}`} icon={"ui-info"} additionalclass="font-14 ml-2 color-red" />

          <Tooltip
            placement="bottom"
            isOpen={showEmptyTooltip}
            toggle={toggleEmptyTooltip}
            target={`tooltip-empty-${_idx}`}
            boundariesElement={document.getElementById("app")}>
            {optionProps?.isEmptyTooltip}
          </Tooltip>
        </>
      )}
    </div>
  );
};

const LoaderComponent = ({ count }) => {
  return (
    <>
      {[...Array(count)].map((_, i) => (
        <div
          key={i}
          className={cx(
            style["skeleton-loader-area"],
            style["align-items-start"],
            style["flex-column"],
            style["mb-3"]
          )}>
          <span className={cx(style["loader-item"], style["w-50"], style["mt-2"], style["mb-2"])}></span>
          <span className={cx(style["loader-item"])} style={{ height: "40px" }}></span>
        </div>
      ))}

      <div className={cx(style["fixed-bottom"], style["mt-auto"])}>
        <div className={cx(style["skeleton-loader-area"], style["flex-column"])}>
          <span className={cx(style["loader-item"])} style={{ height: "44px" }}></span>
        </div>
      </div>
    </>
  );
};

LoaderComponent.propTypes = {
  count: PropTypes.number.isRequired,
};

export const MissingMergeFieldIcon = () => {
  const { missingMfDataRef, setShowMissingMergeFieldModal } = useContext(EditorContext);
  const toggle = () => setShowMissingMergeFieldModal(prevState => !prevState);

  return (
    <li
      id="missing-merge-field-icon"
      className={cx(style["toolbar-hashtag"], style["custom-tooltip"], {
        [style["d-none"]]: missingMfDataRef?.current?.total === 0,
      })}
      onClick={toggle}>
      <div className={cx(style["custom-tooltip"], style["hashtag-trigger"])}>
        <Icon icon="ui-hashtag" />
        <div className={style["hashtag-alert-bg"]}>{missingMfDataRef?.current?.total}</div>
        <div className={cx(style["custom-tooltip-content"], style["bottom"], style["mt-n1"])}>Missing Merge Fields</div>
      </div>
    </li>
  );
};

export const MissingMergeFieldModal = ({ defaultTabIndex }) => {
  const { missingMfDataRef, setShowMfBulkUpdateModal, documentType } = useContext(EditorContext);
  const documentDetails = useSelector(state => state?.document?.documentDetails?.data);
  const authDetails = useSelector(state => state?.auth);

  const { data_source: { provider: dataSource = null, record_url: recordUrl = null } = {} } = useSelector(
    state => state?.document?.documentDetails?.data
  );
  const connectedAccounts = useSelector(state => state.auth?.company?.connected_accounts);
  const { profilePermissionInfo = null, thirdPartyAccounts: { details: authorisedApps = [] } = {} } = useSelector(
    state => state?.company || {}
  );

  const isAdminUser = ["Admin", "Super Admin"].includes(profilePermissionInfo?.name);
  const formattedDataSource = formatProviderDisplayName(dataSource);

  const [schemeType, setSchemeType] = useState(SYSTEM.NAME);
  const [schemeTypeArray, setSchemeTypeArray] = useState(SCHEME_TYPE_ARRAY_REGULAR);
  const [recordUrlCopied, setRecordUrlCopied] = useState(false);
  const [otherMergeFieldsUsed, setOtherMergeFieldsUsed] = useState(false);
  const [sysMFs, setSysMFs] = useState({
    ["Documents"]: {
      mfs: [],
      total: 0,
      infoTooltip: "NA",
      updateTooltip: "Update",
      updatePermission: false,
    },
    ["Document Owner"]: {
      mfs: [],
      total: 0,
      infoTooltip: "Only the Document Owner can update",
      updateTooltip: "Update",
      updatePermission: false,
    },
    ["Company"]: {
      mfs: [],
      total: 0,
      infoTooltip: "Only Admin can update",
      updateTooltip: "Update",
      updatePermission: false,
    },
  });
  const [cusMFs, setCusMFs] = useState({
    mfs: [],
    total: 0,
  });
  const [otherMFs, setOtherMFs] = useState({
    mfs: [],
    total: 0,
  });

  const updateSmfRecord = ({ total, updatePermission, updateUrl }) => {
    if (total > 0 && updatePermission) window.open(updateUrl, "_blank");
  };

  const updateCmfRecords = () => {
    if (cusMFs.total > 0) setShowMfBulkUpdateModal(prevState => !prevState);
  };

  const updateOmfRecords = () => {
    if (otherMFs.total > 0) window.open(decodeURIComponent(recordUrl), "_blank");
  };

  const copyToClipBoard = data => {
    navigator.clipboard.writeText(data);
    setRecordUrlCopied(true);
    setTimeout(() => setRecordUrlCopied(false), 3000);
  };

  const queryOtherMergeFields = () => {
    try {
      return document.querySelectorAll(`.merge-field[data-mf-type="${OTHERS.SLUG}"]`).length > 0;
    } catch (e) {
      return false;
    }
  };

  const getFormattedUrl = targetPath => {
    const baseUrl = window.location.origin;
    const query = "?ref=editor";
    return `${baseUrl}${targetPath}${query}`;
  };

  const getUpdatePermission = module => {
    const DOC_PERMISSION = documentDetails?.permission;
    const DOC_OWNER_ID = documentDetails?.owner_id;
    const DOC_ID = documentDetails?.id;
    const LOGGEDIN_USER_ID = authDetails?.uid;

    if (module === "Documents") {
      return {
        permission: [OWNER, FULL, RW].includes(DOC_PERMISSION),
        url: getFormattedUrl(`/documents/${DOC_ID}/update`),
      };
    } else if (module === "Document Owner") {
      return {
        permission: LOGGEDIN_USER_ID === DOC_OWNER_ID,
        url: getFormattedUrl(`/users/profile/edit`),
      };
    } else if (module === "Company") {
      return {
        permission: isAdminUser,
        url: getFormattedUrl(`/companies/settings/update`),
      };
    }
  };

  useEffect(() => {
    const { mfs: smfs = [], total: _smfTotal = 0 } = missingMfDataRef?.current?.[SYSTEM.SLUG] ?? {};
    const { mfs: cmfs = [], total: cmfTotal = 0 } = missingMfDataRef?.current?.[CUSTOM.SLUG] ?? {};
    const { mfs: omfs = [], total: omfTotal = 0 } = missingMfDataRef?.current?.[OTHERS.SLUG] ?? {};

    // data formation of missing system mfs
    const updatedSysMfs = { ...sysMFs };
    Object.keys(updatedSysMfs).forEach(module => {
      const moduleSpacificMfs = smfs?.filter(mf => mf?.module === module);
      const updatedMissingMfs = moduleSpacificMfs.map(mf => ({ key: mf.key, label: mf.label }));
      const { permission, url } = getUpdatePermission(module);

      updatedSysMfs[module] = {
        ...updatedSysMfs[module],
        mfs: updatedMissingMfs,
        total: updatedMissingMfs.length,
        updatePermission: permission,
        updateUrl: url,
      };
    });
    setSysMFs(updatedSysMfs);

    // data formation of missing custom mfs
    setCusMFs({
      mfs: cmfs,
      total: cmfTotal,
    });

    // data formation of missing other mfs
    setOtherMFs({
      mfs: omfs,
      total: omfTotal,
    });
  }, [missingMfDataRef?.current, profilePermissionInfo, documentDetails, authDetails]);

  useEffect(() => {
    if (documentType !== TYPE_PROPOSAL) return;

    const editorWrap = document.querySelector("#generic-editor-wrap");
    if (!editorWrap) return;

    // observer callback
    const observer = new MutationObserver(() => {
      setOtherMergeFieldsUsed(queryOtherMergeFields());
    });

    // start observing additions/removals of child nodes
    observer.observe(editorWrap, {
      childList: true,
      subtree: true,
    });

    // initial check
    setOtherMergeFieldsUsed(queryOtherMergeFields());

    return () => observer.disconnect();
  }, [authorisedApps, connectedAccounts]);

  useEffect(() => {
    if (documentType === TYPE_PROPOSAL && dataSource && (connectedAccounts?.length > 0 || otherMergeFieldsUsed)) {
      setSchemeTypeArray(SCHEME_TYPE_ARRAY_PROPOSAL);
    }
  }, [documentType, connectedAccounts, otherMergeFieldsUsed, dataSource]);

  useEffect(() => {
    if (defaultTabIndex > 0) {
      setSchemeType(schemeTypeArray[defaultTabIndex]);
    }
  }, [defaultTabIndex, schemeTypeArray]);

  return (
    <div id="missing-merge-field-modal" className={cx(style["missing-merge-field-modal"])}>
      <TabSchemeSelector
        schemeType={schemeType}
        setSchemeType={setSchemeType}
        schemeTypeArray={schemeTypeArray}
        activeTabIndex={schemeTypeArray.indexOf(schemeType)}
      />

      <TabContent>
        {schemeType === SYSTEM.NAME && (
          <TabPane tabId={`${SYSTEM.SLUG}_setting`} className="d-block">
            <div className={cx(style["system-fields"], style["customScroll"], style["scroll-Y"])}>
              <div className={"missing-fields"}>
                <p className={cx(style["font-15"], style["mb-3"])}>Update these field values in DocHipo</p>

                {Object.keys(sysMFs).map((module, _idx) => (
                  <div key={_idx} className={cx(style["module"], style["mb-4"])}>
                    <div
                      className={cx(
                        "module-header",
                        style["d-flex"],
                        style["align-items-center"],
                        style["justify-content-between"],
                        style["mb-2"]
                      )}>
                      <div className={cx(style["d-flex"], style["align-items-center"])}>
                        <h2 className={cx(style["font-15"], style["fw-7"], style["mb-0"])}>{module}</h2>
                        {sysMFs[module].total > 0 && (
                          <span className={cx(style["count-box"], style["red"], style["ml-2"])}>
                            {sysMFs[module].total}
                          </span>
                        )}
                      </div>

                      {sysMFs[module].updatePermission && (
                        <span
                          className={cx(style["edit"], style["custom-tooltip"], {
                            [style["disable"]]: sysMFs[module].total === 0,
                          })}
                          onClick={() => updateSmfRecord(sysMFs[module])}>
                          <Icon icon="ui-edit" />
                          <div className={cx(style["custom-tooltip-content"], style["bottom"], style["mt-5"])}>
                            {sysMFs[module].updateTooltip}
                          </div>
                        </span>
                      )}

                      {!sysMFs[module].updatePermission && (
                        <span className={cx(style["edit"], style["custom-tooltip"])}>
                          <Icon icon={"ui-info"} additionalclass="font-14" />
                          <div className={cx(style["custom-tooltip-content"], style["bottom"], style["mt-5"])}>
                            {sysMFs[module].infoTooltip}
                          </div>
                        </span>
                      )}
                    </div>

                    <div className="module-body">
                      {sysMFs[module].mfs.map(mf => (
                        <p key={mf.key} className={cx(style["font-14"], style["mb-2"])}>
                          {mf.label}
                        </p>
                      ))}

                      {sysMFs[module].total === 0 && (
                        <div className={cx(style["p-2"], style["default-msg"], style["rounded"])}>
                          No missing values
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabPane>
        )}

        {schemeType === CUSTOM.NAME && (
          <TabPane tabId={`${CUSTOM.SLUG}_setting`} className="d-block">
            <div className={cx(style["custom-fields"], style["customScroll"], style["scroll-Y"])}>
              <div className={"missing-fields"}>
                <p className={cx(style["font-15"], style["mb-3"])}>Update these custom merge field values</p>

                <div className={cx(style["module"], style["mb-4"])}>
                  <div
                    className={cx(
                      "module-header",
                      style["d-flex"],
                      style["align-items-center"],
                      style["justify-content-between"],
                      style["mb-2"]
                    )}>
                    <div className={cx(style["d-flex"], style["align-items-center"])}>
                      <h2 className={cx(style["font-15"], style["fw-7"], style["mb-0"])}>Merge Fields</h2>
                      {cusMFs.total > 0 && (
                        <span className={cx(style["count-box"], style["red"], style["ml-2"])}>{cusMFs.total}</span>
                      )}
                    </div>

                    <span
                      className={cx(style["edit"], style["custom-tooltip"], { [style["disable"]]: cusMFs.total === 0 })}
                      onClick={() => updateCmfRecords()}>
                      <Icon icon="ui-edit" />
                      <div className={cx(style["custom-tooltip-content"], style["bottom"], style["mt-5"])}>Update</div>
                    </span>
                  </div>

                  <div className="module-body">
                    {cusMFs.mfs.map(mf => (
                      <p key={mf.key} className={cx(style["font-14"], style["mb-2"])}>
                        {mf.label}
                      </p>
                    ))}

                    {cusMFs.total === 0 && (
                      <div className={cx(style["p-2"], style["default-msg"], style["rounded"])}>No missing values</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TabPane>
        )}

        {schemeType === OTHERS.NAME && (
          <TabPane tabId={`${OTHERS.SLUG}_setting`} className="d-block">
            <div className={cx(style["other-fields"], style["customScroll"], style["scroll-Y"])}>
              <div className={"missing-fields"}>
                <p className={cx(style["font-15"], style["mb-3"])}>
                  Update these field values in the source application
                </p>

                <div className={cx(style["module"], style["mb-4"])}>
                  <div className={cx(style["align-items-center"], style["justify-content-between"])}>
                    <Input
                      cssModule={style}
                      returnType="formGroup"
                      type="text"
                      label="External Data Source"
                      value={formattedDataSource}
                      disabled={true}
                    />
                  </div>
                </div>

                <div className={cx(style["module"], style["mb-4"])}>
                  <div className={cx(style["align-items-center"], style["justify-content-between"], style["Copied"])}>
                    <Input
                      cssModule={style}
                      returnType="formGroup"
                      type="text"
                      label="Record URL"
                      value={decodeURIComponent(recordUrl)}
                      disabled={true}
                    />
                    {!recordUrlCopied && (
                      <span
                        className={style["Copied-icon"]}
                        onClick={() => copyToClipBoard(decodeURIComponent(recordUrl))}>
                        <Icon icon="ui-clone2" />
                      </span>
                    )}
                    {recordUrlCopied && <span className={style["Copied-text"]}>Copied to Clipboard</span>}
                  </div>
                </div>

                <div className={cx(style["module"], style["mb-4"])}>
                  <div
                    className={cx(
                      "module-header",
                      style["d-flex"],
                      style["align-items-center"],
                      style["justify-content-between"],
                      style["mb-2"]
                    )}>
                    <div className={cx(style["d-flex"], style["align-items-center"])}>
                      <h2 className={cx(style["font-15"], style["fw-7"], style["mb-0"])}>Merge Fields</h2>
                      {otherMFs.total > 0 && (
                        <span className={cx(style["count-box"], style["red"], style["ml-2"])}>{otherMFs.total}</span>
                      )}
                    </div>

                    <span
                      className={cx(style["edit"], style["custom-tooltip"], {
                        [style["disable"]]: otherMFs.total === 0,
                      })}
                      onClick={() => updateOmfRecords()}>
                      <Icon icon="ui-edit" />
                      <div className={cx(style["custom-tooltip-content"], style["bottom"], style["mt-5"])}>Update</div>
                    </span>
                  </div>

                  <div className="module-body">
                    {otherMFs.mfs.map(mf => (
                      <p key={mf.key} className={cx(style["font-14"], style["mb-2"])}>
                        {mf.label}
                      </p>
                    ))}

                    {otherMFs.total === 0 && (
                      <div className={cx(style["p-2"], style["default-msg"], style["rounded"])}>No missing values</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TabPane>
        )}
      </TabContent>
    </div>
  );
};

export const MergeFieldInfoModal = () => {
  const msgObj = {
    [SYSTEM.SLUG]: {
      msg: "DocHipo System Merge fields are auto-filled",
      tooltip: "Update the value in the required places in DocHipo to reflect the changes here",
      noData: "No value found",
    },
    [CUSTOM.SLUG]: {
      msg: "Update the custom merge field value here",
      tooltip: "NA",
      noData: "NA",
    },
    [OTHERS.SLUG]: {
      msg: "Merge fields from other apps are auto-filled",
      tooltip: "Update the value in the source application to reflect the changes here",
      noData: "No value found in the source app",
    },
  };

  const { mergeFieldInfoModalData: mfData, setMergeFieldInfoModalData: setMfData } = useContext(EditorContext);
  const { deleteMergeFieldFromInfoModal, updateMergeFieldFromInfoModal } = useMergeFields();
  const [initialValue, setInitialValue] = useState(false);
  const [isUpdateApplicable, setIsUpdateApplicable] = useState(false);
  const [isEllipsis, setIsEllipsis] = useState(false);
  const msgData = msgObj[mfData.type];

  const handleChangeInput = e => {
    const value = e.target.value;
    const cleanedValue = _helpers.getCleanedValue(value);
    const invalidData = _helpers.isDataEmpty({ data: cleanedValue, initialData: initialValue });
    const newMfData = {
      ...mfData,
      data: cleanedValue,
      inValid: invalidData,
      isEmpty: cleanedValue === "",
      uiData: value,
    };

    setMfData(newMfData);
    sessionStorage.setItem(SESSION_KEY_MF_INFO_MODAL_DATA, JSON.stringify(newMfData));

    // checking for valid updation
    setIsUpdateApplicable(!invalidData);
  };

  useEffect(() => {
    const targetNode = document.querySelector(".mf-label");
    setIsEllipsis(targetNode.scrollWidth > targetNode.clientWidth);
    if (!initialValue) setInitialValue(mfData.data);
  }, [mfData]);

  return (
    <>
      <div id="merge-field-info-modal" className={cx(style["merge-field-info-modal"])}>
        <div className={cx(style["header"], style["d-flex"], style["align-items-center"])}>
          <div
            className={cx(style["header-left"], style["custom-tooltip"], {
              [style["cursor-pointer"]]: isEllipsis,
            })}>
            <p className={cx(style["mf-label"], style["fw-7"], style["mb-0"])}>{mfData.label}</p>
            {isEllipsis && (
              <div className={cx(style["custom-tooltip-content"], style["bottom"])}>
                <span>{mfData.label}</span>
              </div>
            )}
          </div>
          <div className={cx(style["header-right"])}>
            <span className={cx(style["delete-mf"], style["rounded"])} onClick={() => deleteMergeFieldFromInfoModal()}>
              <Icon icon="ui-trash" />
            </span>
          </div>
        </div>

        {mfData.type !== CUSTOM.SLUG && (
          <p className={cx(style["mf-app"], style["mb-0"], style["mt-n2"])}>{mfData.source}</p>
        )}

        <div
          className={cx(style["content"], {
            [style["mt-1"]]: mfData.type === CUSTOM.SLUG,
            [style["mt-3"]]: mfData.type !== CUSTOM.SLUG,
          })}>
          <div className={cx(style["msg"], style["d-flex"])}>
            <p className={cx(style["font-15"])}>
              {msgData?.msg}

              {mfData.type !== CUSTOM.SLUG && (
                <span className={cx(style["custom-tooltip"], style["ml-2"])}>
                  <Icon icon={"ui-info"} additionalclass="font-14" />
                  <div className={cx(style["custom-tooltip-content"], style["bottom"])}>
                    <span>{msgData?.tooltip}</span>
                  </div>
                </span>
              )}
            </p>
          </div>

          <div className={style["data"]}>
            {mfData.type !== CUSTOM.SLUG && (
              <>
                {mfData.data !== "NULL" && (
                  <div className={cx(style["has-data"])}>
                    <p className={cx(style["font-base"], style["fw-7"], style["mb-1"])}>Value</p>
                    <p
                      className={cx(
                        style["font-15"],
                        style["rounded"],
                        style["highlight"],
                        style["wrap-text"],
                        style["mb-0"]
                      )}>
                      {mfData.data}
                    </p>
                  </div>
                )}
                {mfData.data === "NULL" && (
                  <div className={cx(style["no-data"], style["text-center"])}>
                    <p
                      className={cx(
                        style["font-15"],
                        style["rounded"],
                        style["highlight"],
                        style["wrap-text"],
                        style["mb-0"]
                      )}>
                      {msgData?.noData}
                    </p>
                  </div>
                )}
              </>
            )}

            {mfData.type === CUSTOM.SLUG && (
              <>
                <div className={cx(style["upsert-data"])}>
                  <p className={cx(style["font-base"], style["fw-7"], style["mb-2"])}>Value</p>
                  <Input
                    type="text"
                    maxLength="255"
                    autoComplete="off"
                    placeholder="Enter the value"
                    value={mfData?.uiData?.trim() === "NULL" ? "" : mfData?.uiData}
                    onChange={e => handleChangeInput(e)}
                  />
                </div>

                <div
                  className={cx(style["fixed-bottom"], style["mt-auto"], {
                    [style["cursor-not-allowed"]]: !isUpdateApplicable,
                  })}>
                  <Button
                    type="button"
                    color={isUpdateApplicable ? "primary" : "secondary"}
                    className={cx(
                      style["w-100"]
                      // { [style["disable"]]: !isUpdateApplicable }
                    )}
                    onClick={() => updateMergeFieldFromInfoModal()}>
                    Update Value
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const MergeFieldUpdateForm = ({ closeModal }) => {
  const { missingMfDataRef } = useContext(EditorContext);
  const { bulkUpdateCustomMergeFields } = useMergeFields();

  const [formData, setFormData] = useState();
  const [formValid, setFormValid] = useState(false);
  const [disable, setDisable] = useState({ rename: false });

  const checkValidity = (value, fieldLabel) => {
    let isValid = true;
    isValid = value.trim() !== "" && isValid;
    isValid = !["null", "NULL", fieldLabel].includes(value) && isValid;
    return isValid;
  };

  const updateForm = (e, mf) => {
    const updatedForm = { ...formData };
    const value = e.target.value;

    updatedForm[mf.key] = {
      ...updatedForm[mf.key],
      value,
      valid: checkValidity(value, mf.label),
    };
    setFormData(updatedForm);
    setFormValid(Object.keys(updatedForm).every(e => updatedForm[e].valid));
  };

  const handleSubmit = e => {
    if (formValid) {
      e.preventDefault();

      const payload = {};
      Object.keys(formData).forEach(key => {
        payload[key] = formData[key].value;
      });

      bulkUpdateCustomMergeFields(payload);
      setDisable({ rename: true });
    }
  };

  useEffect(() => {
    // generate form data state first time
    if (missingMfDataRef?.current?.[CUSTOM.SLUG]?.total >= 1 && !formData) {
      const state = {};
      missingMfDataRef?.current?.[CUSTOM.SLUG]?.mfs?.forEach(mf => {
        state[mf?.key] = { value: "", valid: false };
      });
      setFormData(state);
    }
  }, [missingMfDataRef?.current]);

  return (
    <>
      <h4 className={cx(style["fw-7"], style["mb-4"])}>Update Custom Merge Fields</h4>
      <span aria-label="Close" className={cx(style["cross-modal"], style["rounded"])} onClick={() => closeModal()}>
        <Icon icon="ui-close" />
      </span>

      <Form className={style["mt-1"]} onKeyDown={e => preventSubmit(e)} onSubmit={e => handleSubmit(e)}>
        {missingMfDataRef?.current?.[CUSTOM.SLUG]?.mfs?.map(mf => (
          <Input
            key={mf.key}
            cssModule={style}
            returnType="formGroup"
            type="text"
            label={mf.label}
            required={true}
            value={formData?.[mf.label]?.value}
            onChange={e => updateForm(e, mf)}
            disabled={disable.rename}
          />
        ))}

        <div className={cx(style["align-items-center"], style["d-flex"])}>
          <Button
            type="submit"
            className={style["mt-2"]}
            color="primary"
            cssModule={style}
            disabled={!formValid || disable.rename}>
            Save
          </Button>
        </div>
      </Form>
    </>
  );
};

export const MergeFieldBulkUpdateModal = () => {
  const { showMfBulkUpdateModal: showModal, setShowMfBulkUpdateModal: setShowModal } = useContext(EditorContext);
  const toggleModal = () => setShowModal(prevState => !prevState);

  return (
    <>
      {showModal && (
        <Modal
          toggle={toggleModal}
          showModal={showModal}
          component={MergeFieldUpdateForm}
          setShowModal={setShowModal}
          closeModal={toggleModal}
          backdrop="static"
        />
      )}
    </>
  );
};

const SystemMergeFields = ({ options }) => {
  const { insertMergeField } = useMergeFields();

  const [loading, setLoading] = useState(true);
  const [allFields, setAllFields] = useState();
  const [allModules, setAllModules] = useState();
  const [defaultModule, setDefaultModule] = useState();
  const [defaultField, setDefaultField] = useState();
  const [contextualFields, setContextualFields] = useState();
  const [defaultFieldData, setDefaultFieldData] = useState();

  useEffect(() => {
    if (allModules && allFields) {
      const moduleObj = defaultModule ?? allModules[0];
      setDefaultModule(moduleObj);
      setDefaultField(allFields[moduleObj.slug][0]);
      setContextualFields(allFields[moduleObj.slug]);
      setDefaultFieldData(allFields[moduleObj.slug][0]["value"]);
      setTimeout(() => setLoading(false), SCHEME_TYPE_SWITCH_DELAY);
    }
  }, [defaultModule, allModules, allFields]);

  useEffect(() => {
    if (options?.length >= 1 && options[0]?.merge_fields?.length >= 1) {
      const modules = [];
      const fields = {};
      options.forEach(option => {
        modules.push({ name: option.name, slug: option.slug });
        fields[option.slug] = option.merge_fields;
      });
      setAllFields(fields);
      setAllModules(modules);
    }
  }, [options]);

  return (
    <TabPane tabId={`${SYSTEM.SLUG}_setting`} className="d-block">
      {loading && <LoaderComponent count={2} />}

      {!loading && (
        <div className={style["system-fields"]}>
          <div className={cx(style["align-items-center"], style["justify-content-between"])}>
            <Input
              type="reactstrap-dropdown"
              returnType="formGroup"
              name="module"
              label="Module"
              defaultValue={defaultModule}
              getOptionValue={option => option.name}
              getOptionLabel={option => option.name}
              options={allModules ?? []}
              updateState={option => setDefaultModule(option)}
              noOptionsMessage={() => "No modules found"}
              id={`dhp-mf-${SYSTEM.SLUG}-module`}
              autoFocus={true}
            />
          </div>
          <div className={cx(style["align-items-center"], style["justify-content-between"])}>
            <Input
              type="reactstrap-dropdown"
              returnType="formGroup"
              name="field"
              label="Field"
              defaultValue={defaultField}
              getOptionValue={option => option.name}
              getOptionLabel={option => option.name}
              // isOptionDisabled={option => option.disabled}
              options={contextualFields ?? []}
              components={{ Option: CustomOption }}
              componentsProps={{ Option: { isEmptyTooltip: "No value found" } }}
              updateState={option => (setDefaultField(option), setDefaultFieldData(option.value))}
              noOptionsMessage={() => "No fields found"}
              id={`dhp-mf-${SYSTEM.SLUG}-field`}
              autoFocus={true}
            />
          </div>
          <div className={cx(style["fixed-bottom"], style["mt-auto"])}>
            <Button
              type="button"
              color="primary"
              className={style["w-100"]}
              onClick={() => insertMergeField({ type: SYSTEM.SLUG, defaultModule, defaultField, defaultFieldData })}>
              Insert Merge Field
            </Button>
          </div>
        </div>
      )}
    </TabPane>
  );
};

SystemMergeFields.propTypes = {
  options: PropTypes.array.isRequired,
};

const CustomMergeFields = ({ options, isCompanySuperadmin }) => {
  const { insertMergeField } = useMergeFields();

  const [loading, setLoading] = useState(true);
  const [allFields, setAllFields] = useState([]);
  const [defaultField, setDefaultField] = useState();
  const [defaultFieldData, setDefaultFieldData] = useState();
  const [cmfId, setCmfId] = useState();

  const handleCreateCmf = () => {
    const baseUrl = window.location.origin;
    const targetPath = "/companies/settings/merge-field";
    const query = "?ref=editor";
    const fullUrl = `${baseUrl}${targetPath}${query}`;
    window.open(fullUrl, "_blank");
  };

  const getCmfData = cmfId => {
    const isFoundInDOM = document.querySelector(`.merge-field[data-mf-oid="${cmfId}"]`);
    return isFoundInDOM ? isFoundInDOM.dataset.mfData : undefined;
  };

  const handleChangeInput = e => {
    const value = e.target.value;
    const cleanedValue = _helpers.getCleanedValue(value);
    const mfData = { data: getCmfData(defaultField.id) ?? "", label: defaultField.name };
    const invalidData = _helpers.isDataEmpty({ data: cleanedValue, initialData: mfData?.data });
    const newData = invalidData ? "" : value;
    setDefaultFieldData(newData);
  };

  useEffect(() => {
    if (options?.length >= 1) {
      setAllFields(options);
      setDefaultField(options[0]);
      setDefaultFieldData(getCmfData(options[0]["_id"]));
      setCmfId(options[0]["_id"]);
    }
    if (options?.length >= 0) setTimeout(() => setLoading(false), SCHEME_TYPE_SWITCH_DELAY);
    if (options?.length === 0) setAllFields([]);
  }, [options]);

  return (
    <TabPane tabId={`${CUSTOM.SLUG}_setting`} className="d-block">
      {loading && <LoaderComponent count={2} />}

      {!loading && (
        <div className={style["custom-fields"]}>
          {allFields?.length === 0 && (
            <div className={style["text-center"]}>
              <Icon icon="ui-merge-fields" additionalclass="black" />
              <p className={cx(style["fw-7"], style["font-15"], style["mb-2"])}>Custom Merge Fields</p>
              <p className={cx(style["font-15"])}>
                Use custom merge fields to automatically fill data anywhere on your document.
              </p>

              {isCompanySuperadmin && (
                // <div className={cx(style["fixed-bottom"], style["mt-auto"])}>
                <Button
                  type="button"
                  color=""
                  className={cx(style["w-100"], style["btn-border"], style["black"], style["font-15"])}
                  onClick={() => handleCreateCmf()}>
                  Create Custom Merge Fields
                </Button>
                // </div>
              )}

              {!isCompanySuperadmin && (
                // <div className={cx(style["fixed-bottom"], style["mt-auto"])}>
                <Button
                  type="button"
                  color=""
                  className={cx(style["w-100"], style["btn-border"], style["black"], style["font-15"])}>
                  Only the Admin can create
                </Button>
                // </div>
              )}
            </div>
          )}

          {allFields?.length >= 1 && (
            <>
              <div className={cx(style["align-items-center"], style["justify-content-between"])}>
                <Input
                  type="reactstrap-dropdown"
                  returnType="formGroup"
                  name="field"
                  label="Field"
                  defaultValue={defaultField}
                  getOptionValue={option => option.name}
                  getOptionLabel={option => option.name}
                  options={allFields ?? []}
                  updateState={option => (
                    setDefaultField(option), setDefaultFieldData(getCmfData(option._id)), setCmfId(option._id)
                  )}
                  noOptionsMessage={() => "No fields found"}
                  id={`dhp-mf-${CUSTOM.SLUG}-field`}
                  autoFocus={true}
                />
              </div>

              <div className={cx(style["align-items-center"], style["justify-content-between"])}>
                <Input
                  type="text"
                  returnType="formGroup"
                  name="value"
                  label="Value"
                  maxLength="255"
                  autoComplete="off"
                  placeholder="Enter the value"
                  value={["", "NULL", null, undefined].includes(defaultFieldData) ? "" : defaultFieldData}
                  onChange={e => handleChangeInput(e)}
                />
              </div>

              <div className={cx(style["fixed-bottom"], style["mt-auto"])}>
                <Button
                  type="button"
                  color="primary"
                  className={style["w-100"]}
                  onClick={() =>
                    insertMergeField({
                      type: CUSTOM.SLUG,
                      defaultField,
                      defaultFieldData: _helpers.getCleanedValue(defaultFieldData),
                      cmfId,
                    })
                  }>
                  Insert Merge Field
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </TabPane>
  );
};

CustomMergeFields.propTypes = {
  options: PropTypes.array.isRequired,
  isCompanySuperadmin: PropTypes.bool.isRequired,
};

const OtherMergeFields = ({ options, isCompanySuperadmin }) => {
  const dispatch = useDispatch();
  const { insertMergeField, fetchSchemaData, schemaFetching } = useMergeFields();
  const { data_source: { provider: dataSource = null, record_url: recordUrl = null } = {} } = useSelector(
    state => state?.document?.documentDetails?.data
  );
  const connectedAccounts = useSelector(state => state.auth?.company?.connected_accounts);
  const { profilePermissionInfo = null, thirdPartyAccounts: { details: authorisedApps = [] } = {} } = useSelector(
    state => state?.company || {}
  );

  const isThirdPartyAppConnected = authorisedApps?.some(app => app?.provider === dataSource);
  const isIntegrationEnabled = connectedAccounts?.includes(dataSource);
  const isAdminUser = ["Admin", "Super Admin"].includes(profilePermissionInfo?.name);
  const formattedDataSource = formatProviderDisplayName(dataSource);

  const { isProcessing, isPopupOpen, showSuccessMessage, showFailureMessage, userAlreadyLinked, openAuthPopup } =
    useCRMAuth({
      providerName: dataSource,
    });

  const [loading, setLoading] = useState(true);
  const [allFields, setAllFields] = useState();
  const [allModules, setAllModules] = useState();
  const [defaultModule, setDefaultModule] = useState();
  const [defaultField, setDefaultField] = useState();
  const [contextualFields, setContextualFields] = useState();
  const [defaultFieldData, setDefaultFieldData] = useState();
  const [recordUrlCopied, setRecordUrlCopied] = useState(false);
  const [otherMergeFieldsUsed, setOtherMergeFieldsUsed] = useState(false);

  const queryOtherMergeFields = () => {
    try {
      return document.querySelectorAll(`.merge-field[data-mf-type="${OTHERS.SLUG}"]`).length > 0;
    } catch (e) {
      return false;
    }
  };

  const copyToClipBoard = data => {
    navigator.clipboard.writeText(data);
    setRecordUrlCopied(true);
    setTimeout(() => setRecordUrlCopied(false), 3000);
  };

  const handleConnectApps = () => {
    const baseUrl = window.location.origin;
    const targetPath = "/companies/settings/integration";
    const query = "?ref=editor";
    const fullUrl = `${baseUrl}${targetPath}${query}`;
    window.open(fullUrl, "_self");
  };

  useEffect(() => {
    if (allModules && allFields) {
      const moduleObj = defaultModule ?? allModules[0];
      setDefaultModule(moduleObj);
      setDefaultField(allFields[moduleObj.slug][0]);
      setContextualFields(allFields[moduleObj.slug]);
      setDefaultFieldData(allFields[moduleObj.slug][0]["value"]);
      setTimeout(() => setLoading(false), SCHEME_TYPE_SWITCH_DELAY);
    }
  }, [defaultModule, allModules, allFields]);

  useEffect(() => {
    if (options?.length >= 1 && options[0]?.merge_fields?.length >= 1) {
      const modules = [];
      const fields = {};
      options.forEach(option => {
        modules.push({ name: option.name, slug: option.slug });
        fields[option.slug] = option.merge_fields;
      });
      setAllFields(fields);
      setAllModules(modules);
    } else {
      setAllFields();
      setAllModules();
      setTimeout(() => setLoading(false), SCHEME_TYPE_SWITCH_DELAY);
    }
  }, [options]);

  useEffect(() => {
    if (showSuccessMessage) {
      dispatch(getConnectedAccounts());
      fetchSchemaData();
    }
  }, [showSuccessMessage]);

  useEffect(() => {
    const editorWrap = document.querySelector("#generic-editor-wrap");
    if (!editorWrap) return;

    // observer callback
    const observer = new MutationObserver(() => {
      setOtherMergeFieldsUsed(queryOtherMergeFields());
    });

    // start observing additions/removals of child nodes
    observer.observe(editorWrap, {
      childList: true,
      subtree: true,
    });

    // initial check
    setOtherMergeFieldsUsed(queryOtherMergeFields());

    return () => observer.disconnect();
  }, [authorisedApps, connectedAccounts]);

  return (
    <TabPane tabId={`${OTHERS.SLUG}_setting`} className="d-block">
      {loading && <LoaderComponent count={4} />}

      {!loading && (
        <div className={style["other-fields"]}>
          {/*  case 1: No integrations are available at the moment */}
          {connectedAccounts?.length <= 0 && !otherMergeFieldsUsed && (
            <div className={style["text-center"]}>
              <Icon icon="ui-connected" additionalclass="black" />
              <p className={cx(style["fw-7"], style["font-15"], style["mb-2"])}>Merge Fields from Other Apps</p>
              <p className={cx(style["font-15"])}>
                Connect DocHipo to other <br />
                apps and use merge fields to <br />
                automatically fill data.
              </p>

              {isAdminUser && (
                // <div className={cx(style["fixed-bottom"], style["mt-auto"])}>
                <Button
                  type="button"
                  color=""
                  className={cx(style["w-100"], style["btn-border"], style["black"], style["font-15"])}
                  onClick={handleConnectApps}
                  disabled={isProcessing || isPopupOpen}>
                  {isProcessing ||
                    (isPopupOpen && (
                      <>
                        Connecting
                        <ProcessLoader isOpen={true} wrapperClass="ml-3" />
                      </>
                    ))}
                  {!isProcessing && !isPopupOpen && "Connect to Other Apps"}
                </Button>
                // </div>
              )}

              {!isAdminUser && (
                // <div className={cx(style["fixed-bottom"], style["mt-auto"])}>
                <Button
                  outline
                  disabled
                  className={cx(
                    style["bg-white"],
                    style["cursor-not-allowed"],
                    style["w-100"],
                    style["black"],
                    style["border-dark"],
                    style["font-15"]
                  )}>
                  Only the Admin can connect
                </Button>
                // </div>
              )}
            </div>
          )}
          {/*  case 2: An integration is available, but no data source has been selected */}
          {connectedAccounts?.length > 0 && !dataSource && (
            <div className={style["text-center"]}>
              <p className={cx(style["font-15"], style["py-4"])}>
                No external application is <br />
                selected as a data source for <br />
                this document
              </p>
            </div>
          )}
          {/*  case 3: An integration is available, and data source is selected */}
          {(connectedAccounts?.length > 0 || otherMergeFieldsUsed) && dataSource && (
            <>
              <div className={cx(style["align-items-center"], style["justify-content-between"])}>
                <Input
                  cssModule={style}
                  returnType="formGroup"
                  type="text"
                  label="External Data Source"
                  value={formattedDataSource}
                  disabled={true}
                />
              </div>
              <div className={cx(style["align-items-center"], style["justify-content-between"], style["Copied"])}>
                <Input
                  cssModule={style}
                  returnType="formGroup"
                  type="text"
                  label="Record URL"
                  value={decodeURIComponent(recordUrl)}
                  disabled={true}
                />
                {!recordUrlCopied && (
                  <span className={style["Copied-icon"]} onClick={() => copyToClipBoard(decodeURIComponent(recordUrl))}>
                    <Icon icon="ui-clone2" />
                  </span>
                )}
                {recordUrlCopied && <span className={style["Copied-text"]}>Copied to Clipboard</span>}
              </div>

              {allFields && (
                <>
                  <div className={cx(style["align-items-center"], style["justify-content-between"])}>
                    <Input
                      type="reactstrap-dropdown"
                      returnType="formGroup"
                      name="module"
                      label="Module"
                      defaultValue={defaultModule}
                      getOptionValue={option => option.name}
                      getOptionLabel={option => option.name}
                      options={allModules ?? []}
                      updateState={option => setDefaultModule(option)}
                      noOptionsMessage={() => "No modules found"}
                      id={`dhp-mf-${OTHERS.SLUG}-module`}
                      autoFocus={true}
                    />
                  </div>
                  <div className={cx(style["align-items-center"], style["justify-content-between"])}>
                    <Input
                      type="reactstrap-dropdown"
                      returnType="formGroup"
                      name="field"
                      label="Field"
                      defaultValue={defaultField}
                      getOptionValue={option => option.name}
                      getOptionLabel={option => option.name}
                      options={contextualFields ?? []}
                      components={{ Option: CustomOption }}
                      componentsProps={{ Option: { isEmptyTooltip: "No value found in the source app" } }}
                      updateState={option => (setDefaultField(option), setDefaultFieldData(option.value))}
                      noOptionsMessage={() => "No fields found"}
                      id={`dhp-mf-${OTHERS.SLUG}-field`}
                      autoFocus={true}
                    />
                  </div>
                </>
              )}

              {allFields && (
                <div className={cx(style["fixed-bottom"], style["mt-auto"])}>
                  <Button
                    type="button"
                    color="primary"
                    className={style["w-100"]}
                    onClick={() =>
                      insertMergeField({
                        type: OTHERS.SLUG,
                        defaultModule,
                        defaultField,
                        dataSource: formattedDataSource,
                        defaultFieldData,
                      })
                    }>
                    Insert Merge Field
                  </Button>
                </div>
              )}

              {isAdminUser && !isIntegrationEnabled && !allFields && !schemaFetching && (
                <Button
                  type="button"
                  color=""
                  className={cx(style["w-100"], style["btn-border"], style["black"], style["font-15"], style["mt-4"])}
                  onClick={() => openAuthPopup()}
                  disabled={isProcessing || isPopupOpen}>
                  {isProcessing ||
                    (isPopupOpen && (
                      <>
                        Enabling
                        <ProcessLoader isOpen={true} wrapperClass="ml-3" />
                      </>
                    ))}
                  {!isProcessing && !isPopupOpen && "Enable Integration"}
                </Button>
              )}

              {!isAdminUser && !isIntegrationEnabled && !allFields && (
                <Button
                  outline
                  disabled
                  className={cx(
                    style["bg-white"],
                    style["w-100"],
                    style["cursor-not-allowed"],
                    style["black"],
                    style["border-dark"]
                  )}>
                  Only Admin can Enable
                </Button>
              )}

              {isIntegrationEnabled && !isThirdPartyAppConnected && !allFields && !schemaFetching && (
                <Button
                  type="button"
                  color="primary"
                  className={cx(style["w-100"], style["mt-4"])}
                  onClick={() => openAuthPopup()}
                  disabled={isProcessing || isPopupOpen}>
                  {isProcessing ||
                    (isPopupOpen && (
                      <>
                        Authorizing
                        <ProcessLoader isOpen={true} wrapperClass="ml-3" />
                      </>
                    ))}
                  {!isProcessing && !isPopupOpen && "Authorize DocHipo"}
                </Button>
              )}

              {!allFields && isThirdPartyAppConnected && schemaFetching && <LoaderComponent count={2} />}

              {!allFields && isThirdPartyAppConnected && !schemaFetching && (
                <div className={style["alert-msg"]}>{UNAUTHORIZED_ACCESS}</div>
              )}
            </>
          )}

          {/* Failure message */}
          {!isPopupOpen && showFailureMessage && (
            <div className={cx(style["d-flex"], style["mt-3"], style["text-danger"], style["justify-content-center"])}>
              <Icon icon="ui-alert" additionalclass="mr-2" />
              <span>Failed to connect. Please try again.</span>
            </div>
          )}

          {!isPopupOpen && userAlreadyLinked && (
            <div className={cx(style["d-flex"], style["mt-3"], style["text-danger"], style["justify-content-center"])}>
              <Icon icon="ui-alert" additionalclass="mr-2" />
              <span>User Already Linked</span>
            </div>
          )}

          {/* Success message */}
          {!isPopupOpen && showSuccessMessage && !schemaFetching && (
            <div className={cx(style["d-flex"], style["mt-3"], style["text-success"], style["justify-content-center"])}>
              <Icon icon="check3" additionalclass="mr-2" />
              <span>Successfully connected!</span>
            </div>
          )}
        </div>
      )}
    </TabPane>
  );
};

OtherMergeFields.propTypes = {
  options: PropTypes.array.isRequired,
  isCompanySuperadmin: PropTypes.bool.isRequired,
};

const MergeFieldSelectorModal = ({ defaultTabIndex }) => {
  const { mfSchemaDataRef, documentType } = useContext(EditorContext);
  const SCHEME_TYPE_ARRAY = documentType === TYPE_PROPOSAL ? SCHEME_TYPE_ARRAY_PROPOSAL : SCHEME_TYPE_ARRAY_REGULAR;
  const paidCompanyInfo = useCheckCompanyPlanInfo(true);

  const [schemeType, setSchemeType] = useState(SYSTEM.NAME);
  const [isCompanySuperadmin, setIsCompanySuperadmin] = useState(false);

  useEffect(() => {
    if (paidCompanyInfo?.isLoading === false) {
      setIsCompanySuperadmin(paidCompanyInfo?.companyRole === COMPANY_SUPERADMIN);
    }
  }, [paidCompanyInfo]);

  useEffect(() => {
    if (defaultTabIndex > 0) {
      setSchemeType(SCHEME_TYPE_ARRAY[defaultTabIndex]);
    }
  }, [defaultTabIndex]);

  return (
    <div id="merge-field-selector-modal" className={cx(style["merge-field-selector-modal"])}>
      <TabSchemeSelector
        schemeType={schemeType}
        setSchemeType={setSchemeType}
        schemeTypeArray={SCHEME_TYPE_ARRAY}
        activeTabIndex={SCHEME_TYPE_ARRAY.indexOf(schemeType)}
      />
      <TabContent>
        {schemeType === SYSTEM.NAME && (
          <SystemMergeFields
            {...{
              options: mfSchemaDataRef?.current?.[SYSTEM.SLUG] ?? [],
            }}
          />
        )}
        {schemeType === CUSTOM.NAME && (
          <CustomMergeFields
            {...{
              options: mfSchemaDataRef?.current?.[CUSTOM.SLUG] ?? [],
              isCompanySuperadmin,
            }}
          />
        )}
        {schemeType === OTHERS.NAME && (
          <OtherMergeFields
            {...{
              options: mfSchemaDataRef?.current?.[OTHERS.SLUG] ?? [],
              isCompanySuperadmin,
            }}
          />
        )}
      </TabContent>
    </div>
  );
};

export default MergeFieldSelectorModal;
